/* Journal Portfolio — "Table of Plates." Numbered plates, full-bleed
   photographs, serif italic captions: "Plate I · {title} · MMXXVI". */

import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { useTenantTheme, getSeason } from "@/hooks/useTenantTheme";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

// Year as Roman numeral
function toRoman(num) {
  const map = [["M", 1000], ["CM", 900], ["D", 500], ["CD", 400], ["C", 100], ["XC", 90], ["L", 50], ["XL", 40], ["X", 10], ["IX", 9], ["V", 5], ["IV", 4], ["I", 1]];
  let n = num, out = "";
  for (const [r, v] of map) while (n >= v) { out += r; n -= v; }
  return out;
}

export default function JournalPortfolio() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const portfolio = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];
  const studioName = settings?.studio_name || "Studio";
  const year = toRoman(new Date().getFullYear());
  const season = getSeason();

  return (
    <div
      className="min-h-screen"
      style={{ background: "#F5F2EC", color: "#0E0D0C", fontFamily: "var(--tenant-serif)" }}
      data-testid="journal-portfolio"
    >
      <section className="pt-16 pb-10 px-6 lg:px-12 max-w-5xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-4">
          Index of Plates · {portfolio.length} {portfolio.length === 1 ? "entry" : "entries"}
        </p>
        <h1
          className="font-light italic leading-[1.0]"
          style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(3rem, 8vw, 6rem)" }}
        >
          Portfolio
        </h1>
        <div className="mx-auto mt-6 h-px" style={{ width: "3rem", background: "var(--tenant-accent, #C9A961)" }} aria-hidden />
      </section>

      {portfolio.length === 0 ? (
        <p className="text-center italic text-[color:var(--tenant-fg,#0E0D0C)]/55 py-32" style={{ fontFamily: "var(--tenant-serif)" }}>
          No plates filed for this issue.
        </p>
      ) : (
        portfolio.map((src, i) => (
          <section key={`${src}-${i}`} className="py-12 lg:py-24 px-6 lg:px-12" data-testid={`journal-plate-${i}`}>
            <figure className="max-w-5xl mx-auto">
              <div className="aspect-[3/2] overflow-hidden bg-[#0E0D0C]/10">
                <img src={optimizeImageUrl(src, { w: 1600 })}
                  srcSet={optimizedSrcSet(src, [800, 1200, 1600, 2000]) || undefined}
                  sizes="(max-width: 1024px) 100vw, 1024px"
                  alt="" loading={i < 2 ? "eager" : "lazy"} fetchpriority={i === 0 ? "high" : undefined} decoding="async" className="w-full h-full object-cover" style={getFocalStyle(focals, i)} />
              </div>
              <figcaption className="mt-5 text-center italic text-base sm:text-lg text-[color:var(--tenant-fg,#0E0D0C)]/75" style={{ fontFamily: "var(--tenant-serif)" }}>
                <span className="text-[color:var(--tenant-accent)]">Plate {ROMAN[i] || (i + 1)}</span>
                <span className="mx-2 opacity-50">·</span>
                <span>{season} Issue</span>
                <span className="mx-2 opacity-50">·</span>
                <span>{year}</span>
              </figcaption>
            </figure>
          </section>
        ))
      )}

      <footer className="text-center pb-20 pt-10 italic text-sm text-[color:var(--tenant-fg,#0E0D0C)]/55" style={{ fontFamily: "var(--tenant-serif)" }}>
        End of plates · {studioName} · {season} {new Date().getFullYear()}
      </footer>
    </div>
  );
}
